// Branded report copies of a list: Excel (.xlsx), PDF, Word (.docx) and
// print, each with the Kerala Forest Department and ECOFRAME logos, a title
// block, summary figures, a striped table and a totals row. The export
// libraries load only when a copy is asked for.
//
// report = {
//   title, subtitle, fileName, sheetName,
//   meta:    [[label, value], …]                 period, filters, generated on …
//   summary: [{ label, value, money? }, …]        up to four headline figures
//   columns: [{ header, width, get(row, i), money?, total?, align?, nowrap? }, …]
//   rows:    [...],
//   totalLabel,                                   label of the totals row
// }

import forestLogoUrl from "@/assets/Images/blacklogo.png";
import ecoframeLogoUrl from "@/assets/Images/ecoframe_logo.png";

const DEPARTMENT = "Kerala Forest Department";
const SYSTEM = "ECOFRAME · Film Shooting Permit System";
const FOOTER_NOTE = "System-generated report · ECOFRAME, Kerala Forest Department";

// Forest-green palette of the app (hex without #)
const C = {
  brandDark: "14532D",
  brand: "15803D",
  mint: "BBF7D0",
  tint: "E8F4EC",
  stripe: "F7FBF8",
  line: "D9E8DE",
  ink: "0F172A",
  text: "1E293B",
  muted: "64748B",
  white: "FFFFFF",
};

// Indian digit grouping (₹12,34,567.00) for Excel
const XLSX_INR = '[>=10000000]"₹"##\\,##\\,##\\,##0.00;[>=100000]"₹"##\\,##\\,##0.00;"₹"#,##0.00';

const text = (v) => (v === null || v === undefined ? "" : String(v));

export function inr(value, symbol = "₹") {
  return symbol + Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

function display(item, symbol) {
  return item.money ? inr(item.value, symbol) : typeof item.value === "number" ? item.value.toLocaleString("en-IN") : text(item.value);
}

function totalsOf(report) {
  return report.columns.map((c) =>
    c.total ? report.rows.reduce((sum, row, i) => sum + (Number(c.get(row, i)) || 0), 0) : null
  );
}

// Browsers put narrow no-break spaces in dates ("10:30 am"); plain spaces
// keep them printable in the PDF's built-in fonts.
const ODD_SPACES = new RegExp("[" + String.fromCharCode(0xa0, 0x2009, 0x202f) + "]", "g");
const plainSpaces = (v) => text(v).replace(ODD_SPACES, " ");

function generatedOn() {
  return plainSpaces(new Date().toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }));
}

function download(blob, name) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ── Logos ────────────────────────────────────────────────────────────────

function loadImage(url) {
  return fetch(url)
    .then((res) => {
      if (!res.ok) throw new Error("Logo not found: " + url);
      return res.blob();
    })
    .then(
      (blob) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        })
    )
    .then(
      (dataUrl) =>
        new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve({ dataUrl, base64: dataUrl.split(",")[1], width: img.naturalWidth, height: img.naturalHeight });
          img.onerror = reject;
          img.src = dataUrl;
        })
    );
}

let logosPromise = null;

// { forest, ecoframe }: { dataUrl, base64, width, height } each, or null when
// a logo cannot be loaded (the copy is still made, without it).
export function loadLogos() {
  if (!logosPromise) {
    logosPromise = Promise.all([loadImage(forestLogoUrl).catch(() => null), loadImage(ecoframeLogoUrl).catch(() => null)]).then(
      ([forest, ecoframe]) => {
        if (!forest || !ecoframe) logosPromise = null;
        return { forest, ecoframe };
      }
    );
  }
  return logosPromise;
}

// Size of a logo scaled to fit inside maxW × maxH
function fit(logo, maxW, maxH) {
  const r = Math.min(maxW / logo.width, maxH / logo.height);
  return { w: logo.width * r, h: logo.height * r };
}

function base64ToBytes(base64) {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

// ── Excel (.xlsx) ────────────────────────────────────────────────────────

export async function exportXlsx(report) {
  const [mod, logos] = await Promise.all([import(/* webpackChunkName: "export-xlsx" */ "exceljs/dist/exceljs.min.js"), loadLogos()]);
  const ExcelJS = mod.default || mod;
  const cols = report.columns;
  const n = cols.length;
  const argb = (hex) => ({ argb: "FF" + hex });
  const fill = (hex) => ({ type: "pattern", pattern: "solid", fgColor: argb(hex) });
  const thin = (hex) => ({ style: "thin", color: argb(hex) });
  const box = (hex) => ({ top: thin(hex), left: thin(hex), bottom: thin(hex), right: thin(hex) });

  const wb = new ExcelJS.Workbook();
  wb.creator = "ECOFRAME · " + DEPARTMENT;
  wb.title = report.title;
  wb.created = new Date();

  const ws = wb.addWorksheet(report.sheetName || "Report", {
    views: [{ showGridLines: false }],
    pageSetup: {
      paperSize: 9,
      orientation: "landscape",
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      horizontalCentered: true,
      margins: { left: 0.4, right: 0.4, top: 0.5, bottom: 0.6, header: 0.2, footer: 0.3 },
    },
    headerFooter: { oddFooter: "&L&8" + FOOTER_NOTE + "&R&8Page &P of &N" },
  });
  ws.columns = cols.map((c) => ({ width: c.width || 16 }));

  // Title block between the two logos
  const titles = [
    { value: DEPARTMENT.toUpperCase(), font: { bold: true, size: 13, color: argb(C.brandDark) }, height: 24 },
    { value: SYSTEM, font: { size: 10, color: argb(C.muted) }, height: 16 },
    { value: report.title, font: { bold: true, size: 20, color: argb(C.ink) }, height: 32 },
    { value: report.subtitle || "", font: { italic: true, size: 10, color: argb(C.muted) }, height: 18 },
  ];
  titles.forEach((t, i) => {
    const r = i + 1;
    ws.mergeCells(r, 2, r, n - 1);
    const cell = ws.getCell(r, 2);
    cell.value = t.value;
    cell.font = Object.assign({ name: "Calibri" }, t.font);
    cell.alignment = { horizontal: "center", vertical: "middle" };
    ws.getRow(r).height = t.height;
  });
  if (logos.forest) {
    const s = fit(logos.forest, 120, 108);
    const id = wb.addImage({ base64: logos.forest.base64, extension: "png" });
    ws.addImage(id, { tl: { col: 0.15, row: 0.15 }, ext: { width: s.w, height: s.h }, editAs: "absolute" });
  }
  if (logos.ecoframe) {
    const s = fit(logos.ecoframe, 128, 100);
    const id = wb.addImage({ base64: logos.ecoframe.base64, extension: "png" });
    ws.addImage(id, { tl: { col: n - 1, row: 0.25 }, ext: { width: s.w, height: s.h }, editAs: "absolute" });
  }

  // Green rule under the title block
  let r = 5;
  ws.getRow(r).height = 5;
  for (let c = 1; c <= n; c++) ws.getCell(r, c).fill = fill(C.brand);
  r += 2;

  // Period, filters, generated on …
  (report.meta || []).forEach(([label, value]) => {
    ws.mergeCells(r, 1, r, 2);
    ws.mergeCells(r, 3, r, Math.min(n, 7));
    const l = ws.getCell(r, 1);
    l.value = label;
    l.font = { name: "Calibri", bold: true, size: 10, color: argb(C.muted) };
    const v = ws.getCell(r, 3);
    v.value = text(value);
    v.font = { name: "Calibri", size: 10, color: argb(C.text) };
    r += 1;
  });
  r += 1;

  // Summary figures, one block per figure
  const summary = (report.summary || []).slice(0, 4);
  if (summary.length) {
    const span = Math.max(1, Math.floor(n / summary.length));
    summary.forEach((item, k) => {
      const c1 = k * span + 1;
      const c2 = k === summary.length - 1 ? n : c1 + span - 1;
      ws.mergeCells(r, c1, r, c2);
      ws.mergeCells(r + 1, c1, r + 1, c2);
      const label = ws.getCell(r, c1);
      label.value = item.label.toUpperCase();
      label.font = { name: "Calibri", bold: true, size: 9, color: argb(C.brand) };
      label.alignment = { horizontal: "center", vertical: "bottom" };
      const value = ws.getCell(r + 1, c1);
      value.value = item.money ? Number(item.value) || 0 : item.value;
      if (item.money) value.numFmt = XLSX_INR;
      value.font = { name: "Calibri", bold: true, size: 15, color: argb(C.ink) };
      value.alignment = { horizontal: "center", vertical: "top" };
      for (let c = c1; c <= c2; c++) {
        ws.getCell(r, c).fill = fill(C.tint);
        ws.getCell(r + 1, c).fill = fill(C.tint);
        ws.getCell(r, c).border = { top: thin(C.white), left: thin(C.white), right: thin(C.white) };
        ws.getCell(r + 1, c).border = { bottom: thin(C.white), left: thin(C.white), right: thin(C.white) };
      }
    });
    ws.getRow(r).height = 18;
    ws.getRow(r + 1).height = 26;
    r += 3;
  }

  // Table
  const headRow = r;
  const head = ws.getRow(headRow);
  cols.forEach((c, i) => {
    const cell = head.getCell(i + 1);
    cell.value = c.header;
    cell.font = { name: "Calibri", bold: true, size: 10, color: argb(C.white) };
    cell.fill = fill(C.brandDark);
    cell.alignment = { horizontal: c.money ? "right" : c.align || "left", vertical: "middle", wrapText: true };
    cell.border = box(C.brandDark);
  });
  head.height = 26;

  report.rows.forEach((row, i) => {
    const line = ws.getRow(headRow + 1 + i);
    cols.forEach((c, j) => {
      const cell = line.getCell(j + 1);
      const v = c.get(row, i);
      if (c.money) {
        cell.value = Number(v) || 0;
        cell.numFmt = XLSX_INR;
      } else {
        cell.value = typeof v === "number" ? v : text(v);
      }
      cell.font = { name: "Calibri", size: 10, color: argb(C.text), bold: !!c.money };
      cell.alignment = { horizontal: c.money ? "right" : c.align || "left", vertical: "middle", wrapText: true };
      cell.border = box(C.line);
      if (i % 2 === 1) cell.fill = fill(C.stripe);
    });
  });

  // Totals
  const firstRow = headRow + 1;
  const lastRow = headRow + report.rows.length;
  const totalRow = lastRow + 1;
  const totals = totalsOf(report);
  const firstTotal = cols.findIndex((c) => c.total);
  if (firstTotal > 0) {
    ws.mergeCells(totalRow, 1, totalRow, firstTotal);
    const label = ws.getCell(totalRow, 1);
    label.value = report.totalLabel || "Total";
    label.alignment = { horizontal: "right", vertical: "middle" };
    cols.forEach((c, j) => {
      const cell = ws.getCell(totalRow, j + 1);
      if (c.total) {
        const letter = ws.getColumn(j + 1).letter;
        cell.value = report.rows.length ? { formula: `SUM(${letter}${firstRow}:${letter}${lastRow})`, result: totals[j] } : 0;
        cell.numFmt = XLSX_INR;
        cell.alignment = { horizontal: "right", vertical: "middle" };
      }
      cell.font = { name: "Calibri", bold: true, size: 11, color: argb(C.brandDark) };
      cell.fill = fill(C.tint);
      cell.border = { top: { style: "medium", color: argb(C.brand) }, bottom: thin(C.line), left: thin(C.line), right: thin(C.line) };
    });
    ws.getRow(totalRow).height = 22;
  }

  // Footer note
  const noteRow = totalRow + 2;
  ws.mergeCells(noteRow, 1, noteRow, n);
  const note = ws.getCell(noteRow, 1);
  note.value = FOOTER_NOTE + " · Generated " + generatedOn();
  note.font = { name: "Calibri", italic: true, size: 9, color: argb(C.muted) };
  note.alignment = { horizontal: "center" };

  ws.autoFilter = { from: { row: headRow, column: 1 }, to: { row: headRow, column: n } };
  ws.views = [{ state: "frozen", ySplit: headRow, showGridLines: false }];
  ws.pageSetup.printTitlesRow = `${headRow}:${headRow}`;

  const buffer = await wb.xlsx.writeBuffer();
  download(
    new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
    report.fileName + ".xlsx"
  );
}

// ── PDF ──────────────────────────────────────────────────────────────────

export async function exportPdf(report) {
  const [jspdfMod, autoTableMod, logos] = await Promise.all([
    import(/* webpackChunkName: "export-pdf" */ "jspdf"),
    import(/* webpackChunkName: "export-pdf" */ "jspdf-autotable"),
    loadLogos(),
  ]);
  const JsPDF = jspdfMod.jsPDF || jspdfMod.default;
  const autoTable = autoTableMod.default || autoTableMod;
  const rgb = (hex) => [parseInt(hex.slice(0, 2), 16), parseInt(hex.slice(2, 4), 16), parseInt(hex.slice(4, 6), 16)];
  // The PDF's built-in fonts have no ₹ sign and no narrow spaces
  const RS = "Rs. ";
  const pdfText = (v) => plainSpaces(v).replace(/₹/g, RS);

  const doc = new JsPDF({ orientation: "landscape", unit: "mm", format: "a4", compress: true });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 12;
  const TOP = 37;
  doc.setProperties({ title: report.title, subject: report.subtitle || "", author: DEPARTMENT, creator: "ECOFRAME" });

  const header = () => {
    // Faint emblem behind the page
    if (logos.forest && doc.GState) {
      const s = fit(logos.forest, 120, 120);
      doc.saveGraphicsState();
      doc.setGState(new doc.GState({ opacity: 0.05 }));
      doc.addImage(logos.forest.dataUrl, "PNG", (W - s.w) / 2, (H - s.h) / 2 + 10, s.w, s.h, "forest", "FAST");
      doc.restoreGraphicsState();
    }
    if (logos.forest) {
      const s = fit(logos.forest, 28, 23);
      doc.addImage(logos.forest.dataUrl, "PNG", M, 6.5, s.w, s.h, "forest", "FAST");
    }
    if (logos.ecoframe) {
      const s = fit(logos.ecoframe, 32, 21);
      doc.addImage(logos.ecoframe.dataUrl, "PNG", W - M - s.w, 7.5, s.w, s.h, "ecoframe", "FAST");
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10.5);
    doc.setTextColor(...rgb(C.brandDark));
    doc.text(DEPARTMENT.toUpperCase(), W / 2, 11, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(...rgb(C.muted));
    doc.text(SYSTEM, W / 2, 15.5, { align: "center" });
    doc.setFont("helvetica", "bold");
    doc.setFontSize(17);
    doc.setTextColor(...rgb(C.ink));
    doc.text(pdfText(report.title), W / 2, 23.5, { align: "center" });
    if (report.subtitle) {
      doc.setFont("helvetica", "italic");
      doc.setFontSize(8.5);
      doc.setTextColor(...rgb(C.muted));
      doc.text(pdfText(report.subtitle), W / 2, 28.5, { align: "center" });
    }
    doc.setDrawColor(...rgb(C.brand));
    doc.setLineWidth(0.9);
    doc.line(M, 31.5, W - M, 31.5);
    doc.setDrawColor(...rgb(C.mint));
    doc.setLineWidth(0.3);
    doc.line(M, 32.8, W - M, 32.8);
  };

  const footer = () => {
    doc.setDrawColor(...rgb(C.line));
    doc.setLineWidth(0.3);
    doc.line(M, H - 11, W - M, H - 11);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(...rgb(C.muted));
    doc.text(FOOTER_NOTE + " · Generated " + generatedOn(), M, H - 6.5);
    doc.text("Page " + doc.internal.getNumberOfPages() + " of {total}", W - M, H - 6.5, { align: "right" });
  };

  header();

  // Period, filters, generated on … (label in bold, then the value)
  let x = M;
  let y = TOP + 2;
  doc.setFontSize(8.5);
  (report.meta || []).forEach(([label, value], i) => {
    const l = pdfText(label) + ": ";
    const v = pdfText(value);
    doc.setFont("helvetica", "bold");
    const lw = doc.getTextWidth(l);
    doc.setFont("helvetica", "normal");
    const vw = doc.getTextWidth(v);
    if (x > M && x + lw + vw > W - M) {
      x = M;
      y += 5;
    }
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...rgb(C.muted));
    doc.text(l, x, y);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...rgb(C.text));
    doc.text(v, x + lw, y);
    x += lw + vw + (i < report.meta.length - 1 ? 9 : 0);
  });

  // Summary figures
  const summary = (report.summary || []).slice(0, 4);
  let startY = y + 5;
  if (summary.length) {
    const gap = 4;
    const bw = (W - 2 * M - gap * (summary.length - 1)) / summary.length;
    summary.forEach((item, k) => {
      const bx = M + k * (bw + gap);
      doc.setFillColor(...rgb(C.tint));
      doc.setDrawColor(...rgb(C.line));
      doc.setLineWidth(0.2);
      doc.roundedRect(bx, startY, bw, 15, 2.5, 2.5, "FD");
      doc.setFillColor(...rgb(C.brand));
      doc.rect(bx, startY + 2.5, 1.2, 10, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7);
      doc.setTextColor(...rgb(C.brand));
      doc.text(pdfText(item.label).toUpperCase(), bx + 5, startY + 5.5);
      const value = pdfText(display(item, RS));
      let size = 12.5;
      doc.setFontSize(size);
      while (size > 8 && doc.getTextWidth(value) > bw - 8) doc.setFontSize((size -= 0.5));
      doc.setTextColor(...rgb(C.ink));
      doc.text(value, bx + 5, startY + 11.8);
    });
    startY += 20;
  }

  const cols = report.columns;
  const totals = totalsOf(report);
  const firstTotal = cols.findIndex((c) => c.total);
  const body = report.rows.map((row, i) => cols.map((c) => (c.money ? inr(c.get(row, i), RS) : pdfText(c.get(row, i)))));
  let foot;
  if (firstTotal > 0) {
    foot = [[{ content: pdfText(report.totalLabel || "Total"), colSpan: firstTotal, styles: { halign: "right" } }].concat(
      cols.slice(firstTotal).map((c, k) => (c.total ? inr(totals[firstTotal + k], RS) : ""))
    )];
  }

  // Money and `nowrap` columns get room for their widest value on one line
  const columnStyles = {};
  cols.forEach((c, j) => {
    if (!c.money && !c.nowrap) return;
    const widest = (texts, style, size) => {
      doc.setFont("helvetica", style);
      doc.setFontSize(size);
      return texts.reduce((w, t) => Math.max(w, doc.getTextWidth(t)), 0);
    };
    let w = Math.max(
      widest(body.map((r) => r[j]), c.money ? "bold" : "normal", 7.6),
      widest([pdfText(c.header)], "bold", 7.6)
    );
    if (c.total) w = Math.max(w, widest([inr(totals[j], RS)], "bold", 8.5));
    columnStyles[j] = { minCellWidth: Math.min(w + 4.5, 48) };
  });

  autoTable(doc, {
    head: [cols.map((c) => pdfText(c.header))],
    body,
    foot,
    startY,
    columnStyles,
    showFoot: "lastPage",
    showHead: "everyPage",
    theme: "grid",
    margin: { top: TOP + 2, left: M, right: M, bottom: 15 },
    styles: {
      font: "helvetica",
      fontSize: 7.6,
      cellPadding: { top: 1.8, bottom: 1.8, left: 1.8, right: 1.8 },
      textColor: rgb(C.text),
      lineColor: rgb(C.line),
      lineWidth: 0.15,
      overflow: "linebreak",
      valign: "middle",
    },
    headStyles: { fillColor: rgb(C.brandDark), textColor: 255, fontStyle: "bold", lineColor: rgb(C.brandDark) },
    footStyles: { fillColor: rgb(C.tint), textColor: rgb(C.brandDark), fontStyle: "bold", fontSize: 8.5 },
    alternateRowStyles: { fillColor: rgb(C.stripe) },
    didParseCell: (data) => {
      const c = cols[data.column.index];
      if (!c) return;
      if (c.money) {
        data.cell.styles.halign = "right";
        if (data.section === "body") data.cell.styles.fontStyle = "bold";
      } else if (c.align && data.section !== "foot") {
        data.cell.styles.halign = c.align;
      }
    },
    willDrawPage: (data) => {
      if (data.pageNumber > 1) header();
    },
    didDrawPage: footer,
  });

  if (typeof doc.putTotalPages === "function") doc.putTotalPages("{total}");
  doc.save(report.fileName + ".pdf");
}

// ── Word (.docx) ─────────────────────────────────────────────────────────

export async function exportDocx(report) {
  const [d, logos] = await Promise.all([import(/* webpackChunkName: "export-docx" */ "docx"), loadLogos()]);
  const {
    Document,
    Packer,
    Paragraph,
    TextRun,
    Table,
    TableRow,
    TableCell,
    ImageRun,
    WidthType,
    AlignmentType,
    BorderStyle,
    ShadingType,
    PageOrientation,
    Header,
    Footer,
    PageNumber,
    VerticalAlign,
    TableLayoutType,
    HeightRule,
  } = d;

  const FONT = "Calibri";
  const CONTENT = 16838 - 2 * 720; // A4 landscape width less margins, in twips
  const none = { style: BorderStyle.NONE, size: 0, color: C.white };
  const noBorders = { top: none, bottom: none, left: none, right: none, insideHorizontal: none, insideVertical: none };
  const lineBorder = (hex) => ({ style: BorderStyle.SINGLE, size: 4, color: hex });
  const shade = (hex) => ({ type: ShadingType.CLEAR, color: "auto", fill: hex });
  const run = (value, opts) => new TextRun(Object.assign({ text: text(value), font: FONT, size: 18, color: C.text }, opts));
  const para = (children, opts) => new Paragraph(Object.assign({ children: [].concat(children) }, opts));
  const image = (logo, maxW, maxH) => {
    const s = fit(logo, maxW, maxH);
    return new ImageRun({ data: base64ToBytes(logo.base64), transformation: { width: Math.round(s.w), height: Math.round(s.h) } });
  };

  // Header on every page: emblem · titles · ECOFRAME logo, then a green rule
  const headerTable = new Table({
    width: { size: CONTENT, type: WidthType.DXA },
    columnWidths: [2300, CONTENT - 4600, 2300],
    layout: TableLayoutType.FIXED,
    borders: noBorders,
    rows: [
      new TableRow({
        children: [
          new TableCell({
            width: { size: 2300, type: WidthType.DXA },
            verticalAlign: VerticalAlign.CENTER,
            borders: noBorders,
            children: [para(logos.forest ? image(logos.forest, 84, 74) : run(""), { alignment: AlignmentType.LEFT })],
          }),
          new TableCell({
            width: { size: CONTENT - 4600, type: WidthType.DXA },
            verticalAlign: VerticalAlign.CENTER,
            borders: noBorders,
            children: [
              para(run(DEPARTMENT.toUpperCase(), { bold: true, size: 26, color: C.brandDark }), { alignment: AlignmentType.CENTER }),
              para(run(SYSTEM, { size: 18, color: C.muted }), { alignment: AlignmentType.CENTER, spacing: { after: 60 } }),
              para(run(report.title, { bold: true, size: 36, color: C.ink }), { alignment: AlignmentType.CENTER }),
              para(run(report.subtitle || "", { italics: true, size: 17, color: C.muted }), { alignment: AlignmentType.CENTER }),
            ],
          }),
          new TableCell({
            width: { size: 2300, type: WidthType.DXA },
            verticalAlign: VerticalAlign.CENTER,
            borders: noBorders,
            children: [para(logos.ecoframe ? image(logos.ecoframe, 96, 72) : run(""), { alignment: AlignmentType.RIGHT })],
          }),
        ],
      }),
    ],
  });
  const headerRule = para(run(""), {
    spacing: { before: 60, after: 120 },
    border: { bottom: { style: BorderStyle.THICK, size: 18, color: C.brand, space: 1 } },
  });

  // Period, filters, generated on …
  const metaRuns = [];
  (report.meta || []).forEach(([label, value], i) => {
    if (i) metaRuns.push(run("     •     ", { color: C.mint }));
    metaRuns.push(run(label + ": ", { bold: true, color: C.muted }));
    metaRuns.push(run(value));
  });

  // Summary figures
  const summary = (report.summary || []).slice(0, 4);
  const blocks = [];
  if (summary.length) {
    const w = Math.floor(CONTENT / summary.length);
    const white = { style: BorderStyle.SINGLE, size: 24, color: C.white };
    blocks.push(
      new Table({
        width: { size: CONTENT, type: WidthType.DXA },
        columnWidths: summary.map(() => w),
        layout: TableLayoutType.FIXED,
        rows: [
          new TableRow({
            children: summary.map(
              (item) =>
                new TableCell({
                  width: { size: w, type: WidthType.DXA },
                  shading: shade(C.tint),
                  borders: { top: white, bottom: white, left: { style: BorderStyle.SINGLE, size: 24, color: C.brand }, right: white },
                  margins: { top: 100, bottom: 100, left: 160, right: 120 },
                  children: [
                    para(run(item.label.toUpperCase(), { bold: true, size: 15, color: C.brand })),
                    para(run(display(item, "₹"), { bold: true, size: 30, color: C.ink })),
                  ],
                })
            ),
          }),
        ],
      }),
      para(run(""), { spacing: { after: 120 } })
    );
  }

  // Table
  const cols = report.columns;
  const weight = cols.reduce((s, c) => s + (c.width || 16), 0);
  const widths = cols.map((c) => Math.floor(((c.width || 16) / weight) * CONTENT));
  const align = (c) => (c.money ? AlignmentType.RIGHT : c.align === "center" ? AlignmentType.CENTER : AlignmentType.LEFT);
  const cellBorders = { top: lineBorder(C.line), bottom: lineBorder(C.line), left: lineBorder(C.line), right: lineBorder(C.line) };
  const cell = (value, c, i, opts) =>
    new TableCell(
      Object.assign(
        {
          width: { size: widths[i], type: WidthType.DXA },
          verticalAlign: VerticalAlign.CENTER,
          borders: cellBorders,
          margins: { top: 50, bottom: 50, left: 80, right: 80 },
          children: [para(value, { alignment: align(c) })],
        },
        opts
      )
    );

  const rows = [
    new TableRow({
      tableHeader: true,
      height: { value: 420, rule: HeightRule.ATLEAST },
      children: cols.map((c, i) => cell(run(c.header, { bold: true, color: C.white, size: 17 }), c, i, { shading: shade(C.brandDark) })),
    }),
  ];
  report.rows.forEach((row, r) => {
    rows.push(
      new TableRow({
        cantSplit: true,
        children: cols.map((c, i) => {
          const v = c.get(row, r);
          return cell(run(c.money ? inr(v) : v, { size: 16, bold: !!c.money }), c, i, r % 2 ? { shading: shade(C.stripe) } : {});
        }),
      })
    );
  });
  const firstTotal = cols.findIndex((c) => c.total);
  if (firstTotal > 0) {
    const totals = totalsOf(report);
    const top = { style: BorderStyle.SINGLE, size: 12, color: C.brand };
    rows.push(
      new TableRow({
        children: [
          new TableCell({
            columnSpan: firstTotal,
            width: { size: widths.slice(0, firstTotal).reduce((a, b) => a + b, 0), type: WidthType.DXA },
            shading: shade(C.tint),
            borders: Object.assign({}, cellBorders, { top }),
            margins: { top: 70, bottom: 70, left: 80, right: 80 },
            children: [para(run(report.totalLabel || "Total", { bold: true, size: 18, color: C.brandDark }), { alignment: AlignmentType.RIGHT })],
          }),
        ].concat(
          cols.slice(firstTotal).map((c, k) =>
            cell(run(c.total ? inr(totals[firstTotal + k]) : "", { bold: true, size: 18, color: C.brandDark }), c, firstTotal + k, {
              shading: shade(C.tint),
              borders: Object.assign({}, cellBorders, { top }),
            })
          )
        ),
      })
    );
  }

  const document = new Document({
    creator: "ECOFRAME",
    title: report.title,
    description: report.subtitle || "",
    sections: [
      {
        properties: {
          page: {
            size: { orientation: PageOrientation.LANDSCAPE, width: 11906, height: 16838 },
            margin: { top: 720, bottom: 720, left: 720, right: 720, header: 360, footer: 360 },
          },
        },
        headers: { default: new Header({ children: [headerTable, headerRule] }) },
        footers: {
          default: new Footer({
            children: [
              para(
                [
                  run(FOOTER_NOTE + " · Generated " + generatedOn() + "     |     Page ", { size: 15, color: C.muted }),
                  new TextRun({ children: [PageNumber.CURRENT], font: FONT, size: 15, color: C.muted }),
                  run(" of ", { size: 15, color: C.muted }),
                  new TextRun({ children: [PageNumber.TOTAL_PAGES], font: FONT, size: 15, color: C.muted }),
                ],
                { alignment: AlignmentType.CENTER, border: { top: { style: BorderStyle.SINGLE, size: 4, color: C.line, space: 4 } } }
              ),
            ],
          }),
        },
        children: [para(metaRuns, { spacing: { after: 160 } })]
          .concat(blocks)
          .concat([
            new Table({ width: { size: CONTENT, type: WidthType.DXA }, columnWidths: widths, layout: TableLayoutType.FIXED, rows }),
          ]),
      },
    ],
  });

  const blob = await Packer.toBlob(document);
  download(blob, report.fileName + ".docx");
}

// ── Print ────────────────────────────────────────────────────────────────

const esc = (v) => text(v).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

function reportHtml(report, logos) {
  const cols = report.columns;
  const totals = totalsOf(report);
  const firstTotal = cols.findIndex((c) => c.total);
  const align = (c) => (c.money ? "right" : c.align || "left");
  const logo = (l, cls) => (l ? `<img class="${cls}" src="${l.dataUrl}" alt="">` : `<span class="${cls}"></span>`);

  const meta = (report.meta || []).map(([l, v]) => `<span><b>${esc(l)}:</b> ${esc(v)}</span>`).join("");
  const summary = (report.summary || [])
    .slice(0, 4)
    .map((s) => `<div class="fig"><div class="fig-label">${esc(s.label)}</div><div class="fig-value">${esc(display(s, "₹"))}</div></div>`)
    .join("");
  const head = cols.map((c) => `<th style="text-align:${align(c)}">${esc(c.header)}</th>`).join("");
  const body = report.rows
    .map(
      (row, i) =>
        "<tr>" +
        cols.map((c) => `<td class="${c.money ? "money" : ""}" style="text-align:${align(c)}">${esc(c.money ? inr(c.get(row, i)) : c.get(row, i))}</td>`).join("") +
        "</tr>"
    )
    .join("");
  const foot =
    firstTotal > 0
      ? `<tfoot><tr><td colspan="${firstTotal}" style="text-align:right">${esc(report.totalLabel || "Total")}</td>` +
        cols.slice(firstTotal).map((c, k) => `<td style="text-align:right">${c.total ? esc(inr(totals[firstTotal + k])) : ""}</td>`).join("") +
        "</tr></tfoot>"
      : "";

  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(report.title)}</title>
<style>
  @page { size: A4 landscape; margin: 11mm 10mm 14mm; }
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body { margin: 0; font-family: "Segoe UI", Arial, sans-serif; color: #${C.text}; font-size: 10.5px; }
  .head { display: grid; grid-template-columns: 110px 1fr 110px; align-items: center; gap: 12px; padding-bottom: 8px; border-bottom: 3px solid #${C.brand}; box-shadow: 0 2px 0 #${C.mint}; }
  .logo-l { width: 96px; height: 86px; object-fit: contain; }
  .logo-r { width: 104px; height: 80px; object-fit: contain; justify-self: end; }
  .titles { text-align: center; }
  .dept { font-size: 14px; font-weight: 800; letter-spacing: 1.5px; color: #${C.brandDark}; }
  .system { margin-top: 2px; font-size: 11px; color: #${C.muted}; }
  h1 { margin: 6px 0 2px; font-size: 24px; color: #${C.ink}; }
  .sub { font-size: 11px; font-style: italic; color: #${C.muted}; }
  .meta { display: flex; flex-wrap: wrap; gap: 4px 22px; margin: 10px 0; font-size: 11px; }
  .meta b { color: #${C.muted}; }
  .figs { display: grid; grid-template-columns: repeat(${Math.max(1, Math.min(4, (report.summary || []).length))}, 1fr); gap: 10px; margin-bottom: 12px; }
  .fig { padding: 9px 12px; border-radius: 10px; background: #${C.tint}; border-left: 4px solid #${C.brand}; }
  .fig-label { font-size: 9px; font-weight: 800; letter-spacing: 0.8px; text-transform: uppercase; color: #${C.brand}; }
  .fig-value { margin-top: 2px; font-size: 17px; font-weight: 800; color: #${C.ink}; }
  table { width: 100%; border-collapse: collapse; }
  thead { display: table-header-group; }
  tfoot { display: table-row-group; }
  tr { page-break-inside: avoid; }
  th { background: #${C.brandDark}; color: #fff; font-weight: 700; padding: 6px 6px; border: 1px solid #${C.brandDark}; font-size: 10px; }
  td { padding: 5px 6px; border: 1px solid #${C.line}; word-break: break-word; }
  tbody tr:nth-child(even) td { background: #${C.stripe}; }
  td.money { font-weight: 700; white-space: nowrap; }
  tfoot td { background: #${C.tint}; color: #${C.brandDark}; font-weight: 800; font-size: 11.5px; border-top: 2px solid #${C.brand}; }
  .note { margin-top: 12px; padding-top: 6px; border-top: 1px solid #${C.line}; text-align: center; font-size: 9.5px; color: #${C.muted}; }
  .watermark { position: fixed; left: 50%; top: 55%; width: 420px; transform: translate(-50%, -50%); opacity: 0.045; z-index: -1; }
</style></head><body>
  ${logos.forest ? `<img class="watermark" src="${logos.forest.dataUrl}" alt="">` : ""}
  <div class="head">
    ${logo(logos.forest, "logo-l")}
    <div class="titles">
      <div class="dept">${esc(DEPARTMENT.toUpperCase())}</div>
      <div class="system">${esc(SYSTEM)}</div>
      <h1>${esc(report.title)}</h1>
      ${report.subtitle ? `<div class="sub">${esc(report.subtitle)}</div>` : ""}
    </div>
    ${logo(logos.ecoframe, "logo-r")}
  </div>
  <div class="meta">${meta}</div>
  ${summary ? `<div class="figs">${summary}</div>` : ""}
  <table><thead><tr>${head}</tr></thead><tbody>${body}</tbody>${foot}</table>
  <div class="note">${esc(FOOTER_NOTE)} · Generated ${esc(generatedOn())}</div>
</body></html>`;
}

function imagesReady(doc) {
  return Promise.all(
    Array.prototype.map.call(doc.images, (img) =>
      img.complete ? null : new Promise((resolve) => {
        img.onload = resolve;
        img.onerror = resolve;
      })
    )
  );
}

// Prints from a full-size frame kept out of sight, once its images have loaded.
export async function printReport(report) {
  const logos = await loadLogos();
  const frame = document.createElement("iframe");
  frame.setAttribute("aria-hidden", "true");
  frame.style.cssText = "position:fixed;right:0;bottom:0;width:1123px;height:794px;border:0;opacity:0;pointer-events:none;z-index:-1;";
  document.body.appendChild(frame);
  const win = frame.contentWindow;
  win.document.open();
  win.document.write(reportHtml(report, logos));
  win.document.close();
  await imagesReady(win.document);

  let removed = false;
  const remove = () => {
    if (removed) return;
    removed = true;
    if (frame.parentNode) frame.parentNode.removeChild(frame);
  };
  win.onafterprint = () => setTimeout(remove, 200);
  setTimeout(remove, 120000);
  win.focus();
  win.print();
}
